import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CalendarDays, CheckCircle2, ClipboardCheck, Clock3, Image as ImageIcon, Plus, RefreshCw, Search, Send, XCircle } from 'lucide-react';
import AuthContext from '../../../utils/secure-route/AuthContext';
import useApi from '../../../utils/api-manager/Helper/useApi';
import { useToastHandler } from '../../common/ToastHandler';
import './MonitorAudit.css';

const STATUS = {
  ALL_GOOD: { label: 'All Good', icon: CheckCircle2, tone: 'good' },
  ISSUE_FOUND: { label: 'Issue Found', icon: XCircle, tone: 'issue' },
  NEEDS_CLARIFICATION: { label: 'Needs Clarification', icon: AlertTriangle, tone: 'clarification' },
};

const today = () => new Date().toISOString().slice(0, 10);

const normalizeList = (response) => {
  if (Array.isArray(response)) return response;
  return response?.results || response?.data || [];
};

const formatDate = (value) => value ? new Date(value).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const formatTime = (value) => value ? new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—';

export default function MonitorAudit() {
  const { user } = useContext(AuthContext);
  const { Get, GetPaginatedData, PostFormData, Patch, getHost } = useApi();
  const { handleToast } = useToastHandler();
  const [stores, setStores] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [filters, setFilters] = useState({ from: today(), to: today(), store: '', status: '', search: '' });
  const [form, setForm] = useState({ store: user?.store || '', observedAt: `${today()}T09:00`, status: 'ALL_GOOD', remark: '', evidence: null });

  const storeName = useMemo(() => Object.fromEntries(stores.map(s => [String(s.id || s.store_id), s.name || s.store_name])), [stores]);

  const loadStores = useCallback(async () => {
    try {
      const response = await Get('getAllStoresOfUser');
      setStores(normalizeList(response));
    } catch { setStores([]); }
  }, [Get]);

  const loadRecords = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const queryParams = {
        from: filters.from,
        to: filters.to,
        store: filters.store,
        status: filters.status,
        search: filters.search,
      };
      const response = await GetPaginatedData('monitorAuditData', { page: 1, queryParams });
      setRecords(response?.data || normalizeList(response));
    } catch (err) {
      setError(err?.response?.data?.detail || 'Audit records could not be loaded.');
      setRecords([]);
    } finally { setLoading(false); }
  }, [GetPaginatedData, filters]);

  useEffect(() => { loadStores(); }, [loadStores]);
  useEffect(() => { loadRecords(); }, [loadRecords]);

  const updateForm = (key, value) => setForm(previous => ({ ...previous, [key]: value }));

  const submitRecord = async (event) => {
    event.preventDefault();
    if (!form.store || !form.remark.trim()) {
      handleToast({ type: 'ERROR', message: 'Select a store and add an observation or remark.' });
      return;
    }
    setSaving(true);
    try {
      const payload = new FormData();
      payload.append('store', form.store);
      payload.append('observed_at', new Date(form.observedAt).toISOString());
      payload.append('status', form.status);
      payload.append('remark', form.remark.trim());
      if (form.evidence) payload.append('evidence', form.evidence);
      await PostFormData('monitorAuditData', payload);
      handleToast({ type: 'SUCCESS', message: 'Monitoring audit record added.' });
      setShowForm(false);
      setForm({ store: user?.store || '', observedAt: `${today()}T09:00`, status: 'ALL_GOOD', remark: '', evidence: null });
      await loadRecords();
    } catch (err) {
      handleToast({ type: 'ERROR', message: err?.response?.data?.detail || 'Could not save the audit record.' });
    } finally { setSaving(false); }
  };

  const statusSummary = Object.keys(STATUS).map(key => ({ key, count: records.filter(record => String(record.status || '').toUpperCase() === key).length }));
  const imageUrl = (record) => {
    const value = record.evidence_url || record.evidence || record.image_url || '';
    return value ? (value.startsWith('http') ? value : `${getHost()}${value.startsWith('/') ? value : `/${value}`}`) : '';
  };

  return (
    <main className="monitor-audit-page">
      <section className="monitor-audit-hero">
        <div>
          <div className="monitor-audit-kicker"><ClipboardCheck size={15} /> CONTROLLED REVIEW LOG</div>
          <h1>Monitoring audit</h1>
          <p>Review daily camera checks, evidence, and follow-up notes in one trusted record.</p>
        </div>
        <button className="monitor-audit-primary" onClick={() => setShowForm(true)}><Plus size={18} /> Add audit record</button>
      </section>

      <section className="monitor-audit-summary">
        <div className="monitor-audit-summary-intro"><CalendarDays size={19} /><div><strong>{records.length}</strong><span>records in view</span></div></div>
        {statusSummary.map(({ key, count }) => { const meta = STATUS[key]; const Icon = meta.icon; return <div className={`monitor-audit-stat ${meta.tone}`} key={key}><Icon size={18} /><div><strong>{count}</strong><span>{meta.label}</span></div></div>; })}
      </section>

      <section className="monitor-audit-toolbar">
        <label><span>From</span><input type="date" value={filters.from} onChange={e => setFilters(f => ({ ...f, from: e.target.value }))} /></label>
        <label><span>To</span><input type="date" value={filters.to} onChange={e => setFilters(f => ({ ...f, to: e.target.value }))} /></label>
        <label><span>Store</span><select value={filters.store} onChange={e => setFilters(f => ({ ...f, store: e.target.value }))}><option value="">All stores</option>{stores.map(s => <option key={s.id || s.store_id} value={s.id || s.store_id}>{s.name || s.store_name}</option>)}</select></label>
        <label><span>Status</span><select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}><option value="">All statuses</option>{Object.entries(STATUS).map(([key, value]) => <option key={key} value={key}>{value.label}</option>)}</select></label>
        <label className="monitor-audit-search"><span>Search</span><div><Search size={16} /><input value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} placeholder="Search remarks" /></div></label>
        <button className="monitor-audit-refresh" onClick={loadRecords} aria-label="Refresh audit records"><RefreshCw size={17} /></button>
      </section>

      {error && <div className="monitor-audit-error"><AlertTriangle size={17} /> {error}</div>}
      <section className="monitor-audit-list">
        {loading ? <div className="monitor-audit-empty"><RefreshCw className="spin" size={24} /><p>Loading audit records...</p></div> : records.length === 0 ? <div className="monitor-audit-empty"><ClipboardCheck size={34} /><h3>No audit records yet</h3><p>Employee checks submitted for this period will appear here.</p></div> : records.map(record => {
          const key = String(record.status || 'ALL_GOOD').toUpperCase(); const meta = STATUS[key] || STATUS.ALL_GOOD; const Icon = meta.icon; const evidence = imageUrl(record);
          return <article className="monitor-audit-record" key={record.id || `${record.observed_at}-${record.remark}`}>
            <div className={`monitor-audit-record-status ${meta.tone}`}><Icon size={19} /><span>{meta.label}</span></div>
            <div className="monitor-audit-record-date"><strong>{formatDate(record.observed_at || record.date)}</strong><span><Clock3 size={13} /> {formatTime(record.observed_at || record.time)}</span></div>
            <div className="monitor-audit-record-main"><div className="monitor-audit-store">{record.store_name || storeName[String(record.store)] || 'Store'}</div><p>{record.remark || record.observation || 'No remark provided.'}</p><small>Added by {record.created_by_name || record.employee_name || 'Monitoring team'}</small></div>
            {evidence ? <button className="monitor-audit-evidence" onClick={() => setSelectedImage(evidence)}><img src={evidence} alt="Audit evidence" /><span><ImageIcon size={14} /> View evidence</span></button> : <div className="monitor-audit-no-evidence"><ImageIcon size={17} /><span>No photo attached</span></div>}
          </article>;
        })}
      </section>

      {showForm && <div className="monitor-audit-modal-backdrop" onClick={() => setShowForm(false)}><section className="monitor-audit-modal" onClick={event => event.stopPropagation()}><header><div><span className="monitor-audit-kicker">NEW RECORD</span><h2>Add monitoring check</h2></div><button onClick={() => setShowForm(false)} aria-label="Close"><XCircle size={21} /></button></header><form onSubmit={submitRecord}>
        <label>Store<select value={form.store} onChange={e => updateForm('store', e.target.value)} required><option value="">Select store</option>{stores.map(s => <option key={s.id || s.store_id} value={s.id || s.store_id}>{s.name || s.store_name}</option>)}</select></label>
        <label>Date and time<input type="datetime-local" value={form.observedAt} onChange={e => updateForm('observedAt', e.target.value)} required /></label>
        <label>Status<select value={form.status} onChange={e => updateForm('status', e.target.value)}>{Object.entries(STATUS).map(([key, value]) => <option key={key} value={key}>{value.label}</option>)}</select></label>
        <label>Observation / remark<textarea rows="4" value={form.remark} onChange={e => updateForm('remark', e.target.value)} placeholder="What did you see during the camera check?" required /></label>
        <label className="monitor-audit-file">Screenshot evidence<input type="file" accept="image/*" onChange={e => updateForm('evidence', e.target.files?.[0] || null)} /><span>{form.evidence ? form.evidence.name : 'Choose a screenshot'}</span></label>
        <button className="monitor-audit-primary" type="submit" disabled={saving}>{saving ? 'Saving...' : <><Send size={17} /> Publish record</>}</button>
      </form></section></div>}
      {selectedImage && <div className="monitor-audit-image-modal" onClick={() => setSelectedImage(null)}><img src={selectedImage} alt="Monitoring evidence enlarged" /></div>}
    </main>
  );
}
